import AcxSlider from "components/UI/AcxSlider";
import React, { useEffect } from "react";
import { IApplicationFilterComponent } from "../BaseFilterComponents/IApplicationFilterComponent";
import { observer } from "mobx-react";
import {
    MAX_MESSAGE_COUNT,
    MIN_MESSAGE_COUNT,
    resolveMessageCountSliderState,
} from "./messageCountSliderState";

interface MessageCountFilterProps extends IApplicationFilterComponent {
    label?: string;
    description?: string;
}

function clampMessageCountRange(
    messageCount: number[],
    minBound: number,
    maxBound: number,
    minSelectableMax: number,
): [number, number] {
    const rawMin =
        typeof messageCount[0] === "number" && Number.isFinite(messageCount[0])
            ? Math.max(MIN_MESSAGE_COUNT, messageCount[0])
            : MIN_MESSAGE_COUNT;
    const rawMax =
        typeof messageCount[1] === "number" && Number.isFinite(messageCount[1])
            ? Math.max(MIN_MESSAGE_COUNT, messageCount[1])
            : MIN_MESSAGE_COUNT;
    const isUnset =
        rawMin === MIN_MESSAGE_COUNT && rawMax === MIN_MESSAGE_COUNT;
    if (isUnset) {
        if (minSelectableMax === MIN_MESSAGE_COUNT) {
            return [MIN_MESSAGE_COUNT, MIN_MESSAGE_COUNT];
        }

        const clampedMin = Math.max(minBound, rawMin);
        const clampedMax = Math.max(clampedMin, minSelectableMax);

        return [clampedMin, clampedMax];
    }

    const normalizedMax =
        rawMax === MIN_MESSAGE_COUNT ? MAX_MESSAGE_COUNT : rawMax;
    const clampedMin = Math.max(minBound, rawMin);
    const clampedMax = Math.max(
        minSelectableMax,
        clampedMin,
        Math.min(maxBound, normalizedMax),
    );

    return [clampedMin, clampedMax];
}

export const MessageCountFilter = observer(function (
    props: MessageCountFilterProps,
) {
    const minBound = Math.max(
        MIN_MESSAGE_COUNT,
        props.rangeRestriction?.min ?? MIN_MESSAGE_COUNT,
    );
    const maxBound = Math.min(
        MAX_MESSAGE_COUNT,
        props.rangeRestriction?.max ?? MAX_MESSAGE_COUNT,
    );
    const hasRestrictedSourceRange =
        props.rangeRestriction?.min !== undefined ||
        props.rangeRestriction?.max !== undefined;
    const minSelectableMax = hasRestrictedSourceRange
        ? Math.max(minBound, MIN_MESSAGE_COUNT + 1)
        : MIN_MESSAGE_COUNT;

    useEffect(() => {
        if (!props.rangeRestriction) return;
        const clamped = clampMessageCountRange(
            props.store.messageCount,
            minBound,
            maxBound,
            minSelectableMax,
        );
        if (
            clamped[0] === props.store.messageCount[0] &&
            clamped[1] === props.store.messageCount[1]
        ) {
            return;
        }

        props.store.setMessageCount(clamped);
    }, [
        maxBound,
        minBound,
        minSelectableMax,
        props.rangeRestriction,
        props.store,
        props.store.minMessageCount,
        props.store.maxMessageCount,
    ]);

    const unitLabel = props.description ?? "messages";
    const { value: sliderValue, underSlideLabel } =
        resolveMessageCountSliderState(props.store.messageCount, {
            maxBound,
            unitLabel,
        });

    return (
        <AcxSlider
            label={props.label}
            description={unitLabel}
            value={sliderValue}
            underSlideLabel={underSlideLabel}
            onChange={(messageCount) => {
                // Dragging either thumb settles the range: the parked upper thumb becomes the real
                // bound it is sitting on. Same rule as everywhere else here: editing the filter by
                // hand returns it to an ordinary one.
                props.store.setMessageCount(
                    clampMessageCountRange(
                        messageCount,
                        minBound,
                        maxBound,
                        minSelectableMax,
                    ),
                );
            }}
            minValue={minBound}
            maxValue={maxBound}
            noValueLabel="Any Count"
        />
    );
});
